import {makeAutoObservable} from "mobx";
import {AUTO_PROCESS_TRIGGER, AUTO_PROCESS_VALIDATED_QUALIFIER, C_RMA_AVAILABLE_REASONS, ICreateRmaDefaultSettings} from "../models/User";
import CreateRmaSettingsStore from "../pages/create-rma/stores/create-rma-settings-store";
import {AUTO_PROCESS_EXECUTE, SINCE_UNIT} from "../pages/policies/constants";
import {IChangeable} from "./types";
import { getNullableChangesOrModel, toBoolean, toNullableNumber } from "../models/model-types-methods";
import { REQUIRE_CRMA_FOLLOWUP_QUESTIONS } from "../services/api/create-rma-service";

interface ICreateRmaStoreSettingsChanges extends Partial<ICreateRmaDefaultSettings>{

}

class CreateRmaDefaultSettingsEditorStore implements IChangeable<ICreateRmaDefaultSettings>{
    private _model: ICreateRmaDefaultSettings = {} as ICreateRmaDefaultSettings;
    private _changes: ICreateRmaStoreSettingsChanges = {};
    private _settingsStore: CreateRmaSettingsStore = new CreateRmaSettingsStore();

    constructor(model: ICreateRmaDefaultSettings) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._setModel(model);
    }
    
    private _setModel(model: ICreateRmaDefaultSettings){
        model = _ensureValidModel(model);
        if (model) {
            this._model = model;
        }
        this.discardChanges();
    }
    
    get merchantReturnReasonId(): number | null { 
        return getNullableChangesOrModel(this._changes.merchant_return_reason_id, this._model.merchant_return_reason_id); 
    }
    get resolutionId(): number | null { 
        return getNullableChangesOrModel(this._changes.resolution_id, this._model.resolution_id); 
    }
    get returnMethodId(): number | null { 
        return getNullableChangesOrModel(this._changes.return_method_id, this._model.return_method_id); 
    }
    
    public get isAutoRefund(): boolean { return this._changes.is_auto_refund ?? this._model.is_auto_refund; }
    public get autoRefundTrigger(): AUTO_PROCESS_TRIGGER | null { return this._changes.auto_refund_trigger ?? this._model.auto_refund_trigger; }
    
    public get autoRefundValidatedQualifier(): AUTO_PROCESS_VALIDATED_QUALIFIER | null { return this._changes.auto_refund_validated_qualifier ?? this._model.auto_refund_validated_qualifier ?? null; }
    public get autoRefundExecute(): AUTO_PROCESS_EXECUTE | null { return this._changes.auto_refund_execute ?? this._model.auto_refund_execute;}
    public get autoRefundSinceValue(): number | null { return this._changes.auto_refund_since_value ?? this._model.auto_refund_since_value ?? null; }
    public get autoRefundSinceUnit(): SINCE_UNIT | null { return this._changes.auto_refund_since_unit ?? this._model.auto_refund_since_unit ?? null; }
    public get autoMarkDone(): boolean { return this._changes.auto_mark_done ?? this._model.auto_mark_done; }
    public get availableReturnReasons(): C_RMA_AVAILABLE_REASONS { return this._changes.available_return_reasons ?? this._model.available_return_reasons; }
    public isLoading(): boolean { return this._settingsStore.isLoading; }
    public get settings(): CreateRmaSettingsStore { return this._settingsStore; }
    public get sendCustomerEmails(): boolean { return this._changes.send_customer_emails ?? this._model.send_customer_emails ?? false; }
    public get requireCrmaFuq(): REQUIRE_CRMA_FOLLOWUP_QUESTIONS { return this._changes.require_crma_followup_questions ?? this._model.require_crma_followup_questions; }

    setReturnReason(returnReason: number | null) { this._changes.merchant_return_reason_id = returnReason; }
    setResolution(resolution: number | null) { this._changes.resolution_id = resolution; }
    setReturnMethod(returnMethod: number | null) { this._changes.return_method_id = returnMethod; }
    
    get adjustment(): number|null  { 
        return getNullableChangesOrModel(this._changes.adjustments, this._model.adjustments);
    }
    get restockingFee(): number|null { 
        return getNullableChangesOrModel(this._changes.restocking_fee, this._model.restocking_fee);
    }

    public setRestockingFee(v: number|null ) { 
        this._changes.restocking_fee = v; 
    }
    public setAdjustment(v: number|null ) { 
        this._changes.adjustments = v; 
    }

    public setAutoMarkDone(value: boolean) {
        this._changes.auto_mark_done = value;
    }
    public setAutoRefund(value: boolean){
        this._changes.is_auto_refund = value;
    }
    public setAutoRefundSinceUnit(value: SINCE_UNIT) {
        this._changes.auto_refund_since_unit = value;
    }
    
    public setAutoRefundSinceValue(value: number) {
        this._changes.auto_refund_since_value = value;
    }
    
    public setAutoRefundValidatedQualifier(value: AUTO_PROCESS_VALIDATED_QUALIFIER | null | undefined) {
        this._changes.auto_refund_validated_qualifier = value;
    }
    
    public setAutoRefundTrigger(value: AUTO_PROCESS_TRIGGER | null) {
        this._changes.auto_refund_trigger = value;
    }
    
    public setAutoRefundExecute(value: AUTO_PROCESS_EXECUTE | null) {
        this._changes.auto_refund_execute = value;
    }
    
    public setAvailableReturnReasons(value: C_RMA_AVAILABLE_REASONS) {
        this._changes.available_return_reasons = value;

        this._unsetDefaultReasonIfNeeded();
    }

    public setSendCustomerEmails(value: boolean) {
        this._changes.send_customer_emails = value;
    }

    public setRequireFuq(value: REQUIRE_CRMA_FOLLOWUP_QUESTIONS) {
        this._changes.require_crma_followup_questions = value;
    }
    
    private _unsetDefaultReasonIfNeeded(): void {
        /** if ALL selected for availableReturnReasons - do nothing */
        if (this.availableReturnReasons === C_RMA_AVAILABLE_REASONS.ALL) {
            return;
        }

        const merchantReturnReason = this._settingsStore.findReason(this.merchantReturnReasonId);

        /** if there is default reason selected, but it was not enabled - unset default reason */
        if (merchantReturnReason && !merchantReturnReason.is_enabled) {
            this.setReturnReason(null);
        }
    }
    
    public discardChanges(): void {
        this._changes = {};
    }
    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }
    public getUpdatedModel(): ICreateRmaDefaultSettings {
        return { ...this._model, ...this._changes, _has_changes: this.hasChanges()};
    }
    

}

function _ensureValidModel(model: ICreateRmaDefaultSettings) {
    return {
        ...model,
        is_auto_refund: toBoolean(model.is_auto_refund),
        auto_mark_done: toBoolean(model.auto_mark_done),
        adjustments: toNullableNumber(model.adjustments),
        restocking_fee: toNullableNumber(model.restocking_fee),
    }
}

export default CreateRmaDefaultSettingsEditorStore;